import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDonors } from '../hooks/useDonors';
import { useDebouncedValue } from '../hooks/useDebounce';
import { useAuth } from '../contexts/AuthContext';
import { UserRole } from '../types/auth';
import { ManualMergePicker } from '../components/ManualMergePicker';
import { MergeDialog } from '../components/MergeDialog';
import { DuplicateDonor, DuplicateGroup } from '../services/duplicateService';
import { Donor } from '../services/donorService';
import '../styles/ListPages.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

export function DonorListPage() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const isAdmin = hasRole([UserRole.ADMIN]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [showMergePicker, setShowMergePicker] = useState(false);
  const [mergeGroup, setMergeGroup] = useState<DuplicateGroup<DuplicateDonor> | null>(null);

  const debouncedSearch = useDebouncedValue(searchTerm, 300);

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  // Server-side search + pagination
  const { data, isLoading, isFetching, error } = useDonors({
    page,
    pageSize,
    searchQuery: debouncedSearch || undefined,
  });

  const donors: Donor[] = data?.data || [];

  // Type/priority filters remain client-side (server doesn't support them yet)
  // and apply only within the current page of results.
  const filteredDonors = donors.filter(donor => {
    if (filterType && donor.donorType !== filterType) {
      return false;
    }
    if (filterPriority && donor.priorityLevel !== filterPriority) {
      return false;
    }
    return true;
  });

  const serverPagination = data?.pagination;
  const totalItems = serverPagination?.totalItems ?? donors.length;
  const totalPages = serverPagination?.totalPages ?? Math.max(1, Math.ceil(donors.length / pageSize));

  // Gate the "no donors found" empty state while a search is still in flight
  // (loading, background-fetching, or the debounce hasn't caught up yet) so
  // we never flash "not found" for a query that simply hasn't resolved.
  const isSearching = isLoading || isFetching || debouncedSearch !== searchTerm;

  if (isLoading) {
    return <div className="loading">Loading donors...</div>;
  }

  return (
    <div className="list-page">
      <div className="page-header">
        <h2>Donor Management</h2>
        <p>View and manage all donors and their contributions</p>
      </div>

      <div className="list-actions">
        <Link to="/donors/add">
          <button className="button-secondary">Add New Donor</button>
        </Link>
        <Link to="/donors/donate">
          <button className="button-primary">Record Donation</button>
        </Link>
        {isAdmin && (
          <button className="button-secondary" onClick={() => setShowMergePicker(true)}>
            Merge two donors…
          </button>
        )}
      </div>

      {error && (
        <div className="alert alert-error">
          {error instanceof Error ? error.message : 'Failed to load donors'}
        </div>
      )}

      <div className="filters-card">
        <h3>Search & Filter</h3>
        <div className="filters-grid">
          <div className="filter-group">
            <label htmlFor="search">Search</label>
            <input
              type="text"
              id="search"
              placeholder="Name, Organization, Email, or Phone..."
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
            {debouncedSearch !== searchTerm && searchTerm && (
              <small style={{ color: '#666', fontSize: '0.8rem', marginTop: '0.25rem', display: 'block' }}>
                Searching...
              </small>
            )}
          </div>

          <div className="filter-group">
            <label htmlFor="filterType">Donor Type</label>
            <select
              id="filterType"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
            >
              <option value="">All Types</option>
              <option value="Individual">Individual</option>
              <option value="Corporate">Corporate</option>
              <option value="Foundation">Foundation</option>
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="filterPriority">Priority Level</label>
            <select
              id="filterPriority"
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
            >
              <option value="">All Priorities</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>

          {(searchTerm || filterType || filterPriority) && (
            <div className="filter-group">
              <label>&nbsp;</label>
              <button
                className="button-secondary"
                onClick={() => {
                  handleSearchChange('');
                  setFilterType('');
                  setFilterPriority('');
                }}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        <div className="results-count">
          Showing {filteredDonors.length} of {totalItems} donors
          {(filterType || filterPriority) && ' (filtered)'}
          {totalPages > 1 && ` (Page ${page} of ${totalPages})`}
        </div>
      </div>

      {filteredDonors.length === 0 && isSearching ? (
        <div className="card">
          <p style={{ textAlign: 'center', padding: '2rem' }}>Searching…</p>
        </div>
      ) : filteredDonors.length === 0 ? (
        <div className="card">
          <p style={{ textAlign: 'center', padding: '2rem' }}>
            {donors.length === 0
              ? 'No donors found. Add your first donor to get started.'
              : 'No donors match your search criteria.'}
          </p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Organization</th>
                  <th>Contact</th>
                  <th>Priority</th>
                  <th>Interests</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDonors.map(donor => (
                  <tr key={donor.donorID}>
                    <td className="donor-name">
                      <strong>
                        {donor.salutation && `${donor.salutation} `}
                        {donor.fullName}
                      </strong>
                    </td>
                    <td>
                      <span className={`badge badge-${donor.donorType.toLowerCase()}`}>
                        {donor.donorType}
                      </span>
                    </td>
                    <td>{donor.organizationCompany || '—'}</td>
                    <td className="contact-info">
                      <div>{donor.phoneNumber}</div>
                      <div className="email-small">{donor.emailAddress}</div>
                    </td>
                    <td>
                      <span className={`priority-${donor.priorityLevel.toLowerCase()}`}>
                        {donor.priorityLevel}
                      </span>
                    </td>
                    <td>
                      {donor.interests && donor.interests.length > 0 ? (
                        <div className="interests-list">
                          {donor.interests.slice(0, 2).join(', ')}
                          {donor.interests.length > 2 && ` +${donor.interests.length - 2}`}
                        </div>
                      ) : '—'}
                    </td>
                    <td className="actions">
                      <button
                        className="button-link"
                        onClick={() => navigate(`/donors/${donor.donorID}`)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card View - Compact */}
          <div className="mobile-cards">
            {filteredDonors.map(donor => (
              <div
                key={donor.donorID}
                className="mobile-card-compact"
                onClick={() => navigate(`/donors/${donor.donorID}`)}
              >
                <div className="mobile-card-main">
                  <div className="mobile-card-name">
                    {donor.salutation && `${donor.salutation} `}
                    {donor.fullName}
                  </div>
                  <div className="mobile-card-info">
                    <span className={`priority-${donor.priorityLevel.toLowerCase()}`}>
                      {donor.priorityLevel}
                    </span>
                    {donor.organizationCompany && (
                      <span className="mobile-card-location">• {donor.organizationCompany}</span>
                    )}
                  </div>
                </div>
                <div className="mobile-card-right">
                  <span className={`badge badge-${donor.donorType.toLowerCase()} mobile-badge`}>
                    {donor.donorType.charAt(0)}
                  </span>
                  {donor.phoneNumber && (
                    <a
                      href={`tel:${donor.phoneNumber}`}
                      className="mobile-card-phone"
                      onClick={(e) => e.stopPropagation()}
                    >
                      📞
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem',
            marginTop: '1.5rem',
            padding: '1rem',
            flexWrap: 'wrap'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label htmlFor="pageSize" style={{ fontSize: '0.9rem', color: '#666' }}>
                Show:
              </label>
              <select
                id="pageSize"
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                style={{
                  padding: '0.4rem 0.6rem',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontSize: '0.9rem'
                }}
              >
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} per page
                  </option>
                ))}
              </select>
            </div>

            {totalPages > 1 && (
              <>
                <button
                  className="button-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1 || isLoading}
                >
                  Previous
                </button>

                <span style={{ fontSize: '0.9rem', color: '#666' }}>
                  Page {page} of {totalPages}
                </span>

                <button
                  className="button-secondary"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || isLoading}
                >
                  Next
                </button>
              </>
            )}
          </div>
        </>
      )}

      {showMergePicker && (
        <ManualMergePicker
          kind="donor"
          onCancel={() => setShowMergePicker(false)}
          onContinue={(group) => {
            setShowMergePicker(false);
            setMergeGroup(group as DuplicateGroup<DuplicateDonor>);
          }}
        />
      )}

      {mergeGroup && (
        <MergeDialog
          kind="donor"
          group={mergeGroup}
          onClose={() => setMergeGroup(null)}
          onComplete={() => {
            setMergeGroup(null);
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}
